/**
 * Tipos do Financeiro do Portal do Credenciado.
 *
 * Cobre dois recortes da mesma entidade — a medição mensal:
 *  - `MedicaoResumo`, a linha da listagem em `/credenciado/financeiro`;
 *  - `MedicaoDetalhe`, o demonstrativo formal de uma medição, em
 *    `/credenciado/financeiro/:id` — o bruto, cada dedutivo com sua descrição
 *    e valor, o líquido, e a memória de cálculo linha a linha.
 *
 * Sem `docs/MODELO-DE-DADOS.md` para estas tabelas ainda (nenhuma existe no
 * banco): os campos aqui nascem do protótipo e do bom senso, não de migração.
 */

/** Estágio de uma medição, do fechamento da apuração até o crédito pago. */
export type StatusMedicao = 'em_apuracao' | 'aguardando_ateste' | 'pago';

/** Indicadores consolidados dos últimos 12 meses, exibidos no topo da listagem. */
export interface ResumoFinanceiro {
  acumulado_12_meses: number;
  total_recebido: number;
  total_a_receber: number;
  /** `null` quando não há medição aprovada com previsão de crédito futura. */
  proximo_credito_valor: number | null;
  proximo_credito_previsto_em: string | null;
}

export interface MedicaoResumo {
  id: string;
  /** Primeiro dia do mês de competência, formato ISO ("2026-07-01"). */
  competencia: string;
  valor_bruto: number;
  total_dedutivos: number;
  valor_liquido: number;
  status: StatusMedicao;
}

/** Uma dedução do valor bruto, com a descrição de por que foi aplicada. */
export interface ItemDedutivo {
  descricao: string;
  valor: number;
}

/** Uma linha da memória de cálculo — como o valor bruto foi composto. */
export interface ItemMemoriaDeCalculo {
  descricao: string;
  quantidade: number;
  valor_unitario: number;
  valor_total: number;
}

export interface MedicaoDetalhe extends MedicaoResumo {
  previsao_credito_em: string | null;
  dedutivos: ItemDedutivo[];
  memoria_de_calculo: ItemMemoriaDeCalculo[];
}

/** Filtro da listagem de medições. A paginação é sempre do servidor. */
export interface FiltroDeMedicoes {
  pagina: number;
  tamanho: number;
  status?: StatusMedicao;
}

export const TAMANHO_PAGINA_PADRAO = 10;

/**
 * Tipo de revisão que o credenciado pode pedir sobre uma medição.
 *
 * O protótipo tinha um terceiro tipo, "Ajuste DTE (geral)" — pula a SME e
 * exige papel de gestor interno. Não faz sentido nesta tela: quem está aqui
 * é sempre o credenciado, nunca um gestor da operação.
 */
export type TipoDeRevisao = 'apontamento' | 'multa';

export const TIPO_DE_REVISAO_META: Record<TipoDeRevisao, { rotulo: string; descricao: string }> = {
  apontamento: {
    rotulo: 'Revisão de apontamento',
    descricao: 'Ajuste em quantidade de alunos, faltas, modalidade ou KM. Encaminhado à SME.',
  },
  multa: {
    rotulo: 'Revisão de multa',
    descricao: 'Contestação de glosa, penalidade ou desconto vinculado à OS. Encaminhado à SME.',
  },
};

export interface MensagemDoChat {
  id: string;
  medicao_id: string;
  autor: 'credenciado' | 'sme';
  texto: string;
  enviada_em: string;
}
